import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';
import { Copy, Trash2, Webhook, RefreshCw, Send, History } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { PortalFooter } from '../components/PortalFooter.tsx';
import { PortalHealthBanner } from '../components/PortalHealthBanner.tsx';
import { PortalNavBar } from '../components/PortalNavBar.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface PortalWebhookEndpoint {
  id: string;
  organizationId: string;
  url: string;
  signingSecretPrefix: string;
  events: string[];
  enabled: boolean;
  description: string | null;
  createdAt: string;
  updatedAt: string;
  lastSuccessAt: string | null;
  lastFailureAt: string | null;
}

interface PortalWebhookDelivery {
  id: string;
  endpointId: string;
  event: string;
  status: 'pending' | 'succeeded' | 'failed' | 'dead';
  httpCode: number | null;
  attempts: number;
  nextAttemptAt: string;
  lastAttemptAt: string | null;
  lastError: string | null;
  createdAt: string;
}

interface ListResponse {
  endpoints: PortalWebhookEndpoint[];
  subscribableEvents: string[];
}

interface CreateResponse {
  endpoint: PortalWebhookEndpoint;
  signingSecret: string;
}

interface RotateResponse {
  endpoint: PortalWebhookEndpoint;
  signingSecret: string;
}

interface PortalContact {
  id: string;
  email: string;
  name: string | null;
  isRequester: boolean;
  isOrgAdmin: boolean;
  isOrgBilling: boolean;
}

const STATUS_BADGE: Record<PortalWebhookDelivery['status'], string> = {
  pending: 'bg-amber-50 text-amber-700',
  succeeded: 'bg-emerald-50 text-emerald-700',
  failed: 'bg-rose-50 text-rose-700',
  dead: 'bg-[var(--color-surface)] text-[var(--color-muted)]',
};

export function PortalWebhooksPage(): React.ReactElement {
  useDocumentTitle('Webhooks');
  const navigate = useNavigate();
  const [contact, setContact] = useState<PortalContact | null>(null);
  const [authChecked, setAuthChecked] = useState(false);

  const [endpoints, setEndpoints] = useState<PortalWebhookEndpoint[] | null>(null);
  const [subscribableEvents, setSubscribableEvents] = useState<string[]>([]);
  const [forbiddenReason, setForbiddenReason] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [showCreate, setShowCreate] = useState(false);
  const [url, setUrl] = useState('https://');
  const [description, setDescription] = useState('');
  const [selectedEvents, setSelectedEvents] = useState<string[]>([]);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<CreateResponse | null>(null);
  const [justRotated, setJustRotated] = useState<RotateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  const [openDeliveriesFor, setOpenDeliveriesFor] = useState<string | null>(null);
  const [deliveries, setDeliveries] = useState<PortalWebhookDelivery[] | null>(null);

  async function loadAuth() {
    try {
      const r = await apiFetch<{ contact: PortalContact | null }>('/api/portal/auth/me');
      if (!r.contact) {
        navigate('/login', { replace: true, state: { from: '/portal/webhooks' } });
        return;
      }
      setContact(r.contact);
    } catch {
      navigate('/login', { replace: true, state: { from: '/portal/webhooks' } });
    } finally {
      setAuthChecked(true);
    }
  }

  async function load() {
    setForbiddenReason(null);
    try {
      const r = await apiFetch<ListResponse>('/api/portal/webhooks');
      setEndpoints(r.endpoints);
      setSubscribableEvents(r.subscribableEvents);
      if (selectedEvents.length === 0 && r.subscribableEvents.length > 0) {
        setSelectedEvents(r.subscribableEvents);
      }
    } catch (e) {
      if (e instanceof ApiError && e.status === 403) {
        setForbiddenReason(e.message);
        setEndpoints([]);
        return;
      }
      setError(e instanceof ApiError ? e.message : 'Failed to load webhooks.');
    }
  }

  useEffect(() => {
    void loadAuth();
  }, []);

  useEffect(() => {
    if (contact?.isOrgAdmin) void load();
  }, [contact]);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!url.trim() || selectedEvents.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const r = await apiFetch<CreateResponse>('/api/portal/webhooks', {
        method: 'POST',
        json: {
          url: url.trim(),
          events: selectedEvents,
          description: description.trim() || undefined,
        },
      });
      setJustCreated(r);
      setUrl('https://');
      setDescription('');
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create endpoint.');
    } finally {
      setCreating(false);
    }
  }

  async function toggleEnabled(ep: PortalWebhookEndpoint) {
    try {
      await apiFetch(`/api/portal/webhooks/${ep.id}`, {
        method: 'PATCH',
        json: { enabled: !ep.enabled },
      });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to update.');
    }
  }

  async function rotateSecret(ep: PortalWebhookEndpoint) {
    if (!window.confirm(`Rotate signing secret for ${ep.url}? Your receiver will start getting signatures from the new secret immediately. Update your config before rotating in production.`)) return;
    try {
      const r = await apiFetch<RotateResponse>(`/api/portal/webhooks/${ep.id}/rotate-secret`, {
        method: 'POST',
      });
      setJustRotated(r);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to rotate secret.');
    }
  }

  async function sendTest(ep: PortalWebhookEndpoint) {
    try {
      await apiFetch(`/api/portal/webhooks/${ep.id}/test`, { method: 'POST' });
      setError(null);
      window.alert('Test delivery queued. Open "Deliveries" in a few seconds to see the result.');
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to send test.');
    }
  }

  async function remove(ep: PortalWebhookEndpoint) {
    if (!window.confirm(`Delete the webhook to ${ep.url}? Pending and historical deliveries will be removed and your receiver will stop being called.`)) return;
    try {
      await apiFetch(`/api/portal/webhooks/${ep.id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to delete endpoint.');
    }
  }

  async function openDeliveries(ep: PortalWebhookEndpoint) {
    setOpenDeliveriesFor(ep.id);
    setDeliveries(null);
    try {
      const r = await apiFetch<{ deliveries: PortalWebhookDelivery[] }>(
        `/api/portal/webhooks/${ep.id}/deliveries`,
      );
      setDeliveries(r.deliveries);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to load deliveries.');
    }
  }

  async function redeliver(endpointId: string, deliveryId: string) {
    try {
      await apiFetch(`/api/portal/webhooks/${endpointId}/deliveries/${deliveryId}/redeliver`, {
        method: 'POST',
      });
      await openDeliveries({ id: endpointId } as PortalWebhookEndpoint);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to redeliver.');
    }
  }

  function toggleEvent(name: string) {
    setSelectedEvents((cur) =>
      cur.includes(name) ? cur.filter((e) => e !== name) : [...cur, name],
    );
  }

  async function copyText(text: string) {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // selectable input is the fallback.
    }
  }

  function shell(children: React.ReactNode): React.ReactElement {
    return (
      <div className="min-h-screen bg-[var(--color-surface)]">
        <PortalNavBar contact={contact} />
        <PortalHealthBanner />
        <main className="mx-auto max-w-5xl space-y-6 px-6 py-8">{children}</main>
        <PortalFooter />
      </div>
    );
  }

  if (!authChecked) {
    return shell(<p className="text-[var(--color-muted)]">Loading…</p>);
  }
  if (!contact) {
    return shell(<p className="text-[var(--color-muted)]">Loading…</p>);
  }
  if (!contact.isOrgAdmin) {
    return shell(
      <Card>
        <h1 className="text-2xl font-bold text-[var(--color-header)]">Webhooks</h1>
        <p className="mt-2 text-sm text-[var(--color-muted)]">
          Only your organization's admin contact can manage webhooks. Ask your internal admin
          if you need access.
        </p>
      </Card>,
    );
  }
  if (forbiddenReason) {
    return shell(
      <Card>
        <h1 className="text-2xl font-bold text-[var(--color-header)]">Webhooks</h1>
        <p className="mt-2 text-sm text-[var(--color-muted)]">{forbiddenReason}</p>
      </Card>,
    );
  }

  return shell(
    <>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">Webhooks</h1>
          <p className="mt-1 max-w-2xl text-[var(--color-muted)]">
            Receive signed HTTP pushes whenever your organization's time entries change.
            Each delivery is signed with HMAC-SHA256; verify the{' '}
            <code className="rounded bg-white px-1 py-0.5 text-xs">x-t3-signature</code> header
            using your endpoint's signing secret before trusting the body.
          </p>
        </div>
        <Button onClick={() => setShowCreate((v) => !v)}>
          <Webhook className="h-4 w-4" /> {showCreate ? 'Cancel' : 'New endpoint'}
        </Button>
      </div>

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

      {(justCreated || justRotated) && (
        <Card className="border-[var(--color-t3-blue)]/40 bg-[var(--color-t3-blue)]/5">
          <h2 className="text-base font-semibold text-[var(--color-header)]">
            {justCreated ? 'Endpoint created — copy the signing secret now' : 'Signing secret rotated — copy it now'}
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            This is the only time the full secret will be shown. After you leave this page
            you'll only see the prefix.
          </p>
          <div className="mt-3 flex items-stretch gap-2">
            <input
              readOnly
              value={(justCreated ?? justRotated)!.signingSecret}
              onClick={(e) => (e.target as HTMLInputElement).select()}
              className="flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 font-mono text-xs"
            />
            <Button onClick={() => void copyText((justCreated ?? justRotated)!.signingSecret)}>
              <Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <div className="mt-3 text-xs text-[var(--color-muted)]">
            Verify deliveries by computing{' '}
            <code className="rounded bg-white px-1 py-0.5">HMAC-SHA256("{`${'$'}{timestamp}.${'$'}{rawBody}`}", secret)</code>{' '}
            and comparing the hex digest against the <code className="rounded bg-white px-1 py-0.5">v1=</code>{' '}
            value in <code className="rounded bg-white px-1 py-0.5">x-t3-signature</code>.
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => {
                setJustCreated(null);
                setJustRotated(null);
              }}
              className="text-xs text-[var(--color-muted)] hover:underline"
            >
              I've saved it — dismiss
            </button>
          </div>
        </Card>
      )}

      {showCreate && (
        <Card>
          <form onSubmit={create} className="space-y-4">
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">URL (https://)</span>
              <input
                type="url"
                required
                placeholder="https://example.com/webhooks/t3"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 font-mono text-xs"
              />
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">Description (optional)</span>
              <input
                type="text"
                maxLength={500}
                placeholder="e.g. Power BI ingest"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
            </label>
            <div>
              <span className="block text-xs font-medium text-[var(--color-header)]">Events</span>
              <div className="mt-2 space-y-2">
                {subscribableEvents.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedEvents.includes(s)}
                      onChange={() => toggleEvent(s)}
                      className="h-4 w-4"
                    />
                    <code className="rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-xs">{s}</code>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 border-t border-[var(--color-border)] pt-3">
              <Button
                type="submit"
                disabled={creating || !url.trim() || selectedEvents.length === 0}
              >
                {creating ? 'Creating…' : 'Create endpoint'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="overflow-hidden !p-0">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
            <tr>
              <th className="px-4 py-3">URL</th>
              <th className="px-4 py-3">Events</th>
              <th className="px-4 py-3">Secret</th>
              <th className="px-4 py-3">Last success</th>
              <th className="px-4 py-3">Last failure</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {endpoints === null ? (
              <tr>
                <td className="px-4 py-6 text-[var(--color-muted)]" colSpan={7}>
                  Loading…
                </td>
              </tr>
            ) : endpoints.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-[var(--color-muted)]" colSpan={7}>
                  No webhook endpoints yet.
                </td>
              </tr>
            ) : (
              endpoints.map((ep) => (
                <tr key={ep.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-3">
                    <div className="font-mono text-xs">{ep.url}</div>
                    {ep.description && (
                      <div className="text-xs text-[var(--color-muted)]">{ep.description}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    {ep.events.map((s) => (
                      <code key={s} className="mr-1 rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-xs">
                        {s}
                      </code>
                    ))}
                  </td>
                  <td className="px-4 py-3 font-mono text-xs">{ep.signingSecretPrefix}…</td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {ep.lastSuccessAt ? new Date(ep.lastSuccessAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {ep.lastFailureAt ? new Date(ep.lastFailureAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {ep.enabled ? (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                        enabled
                      </span>
                    ) : (
                      <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-xs text-[var(--color-muted)]">
                        disabled
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex flex-wrap items-center justify-end gap-2 text-xs">
                      <button
                        type="button"
                        onClick={() => void sendTest(ep)}
                        className="inline-flex items-center gap-1 text-[var(--color-t3-blue)] hover:underline"
                      >
                        <Send className="h-3.5 w-3.5" /> Test
                      </button>
                      <button
                        type="button"
                        onClick={() => void openDeliveries(ep)}
                        className="inline-flex items-center gap-1 text-[var(--color-t3-blue)] hover:underline"
                      >
                        <History className="h-3.5 w-3.5" /> Deliveries
                      </button>
                      <button
                        type="button"
                        onClick={() => void rotateSecret(ep)}
                        className="inline-flex items-center gap-1 text-[var(--color-t3-blue)] hover:underline"
                      >
                        <RefreshCw className="h-3.5 w-3.5" /> Rotate
                      </button>
                      <button
                        type="button"
                        onClick={() => void toggleEnabled(ep)}
                        className="inline-flex items-center gap-1 text-[var(--color-muted)] hover:underline"
                      >
                        {ep.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        type="button"
                        onClick={() => void remove(ep)}
                        className="inline-flex items-center gap-1 text-[var(--color-danger)] hover:underline"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>

      {openDeliveriesFor && (
        <Card>
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-[var(--color-header)]">
              Recent deliveries
            </h2>
            <button
              type="button"
              onClick={() => {
                setOpenDeliveriesFor(null);
                setDeliveries(null);
              }}
              className="text-xs text-[var(--color-muted)] hover:underline"
            >
              Close
            </button>
          </div>
          <div className="mt-3 overflow-hidden rounded-[var(--radius-card)] border border-[var(--color-border)]">
            <table className="w-full text-xs">
              <thead className="bg-[var(--color-surface)] text-left uppercase tracking-wide text-[var(--color-muted)]">
                <tr>
                  <th className="px-3 py-2">When</th>
                  <th className="px-3 py-2">Event</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">HTTP</th>
                  <th className="px-3 py-2">Attempts</th>
                  <th className="px-3 py-2">Last error</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {deliveries === null ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-4 text-[var(--color-muted)]">
                      Loading…
                    </td>
                  </tr>
                ) : deliveries.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-3 py-4 text-[var(--color-muted)]">
                      No deliveries yet.
                    </td>
                  </tr>
                ) : (
                  deliveries.map((d) => (
                    <tr key={d.id} className="border-t border-[var(--color-border)]">
                      <td className="px-3 py-2 whitespace-nowrap">
                        {new Date(d.createdAt).toLocaleString()}
                      </td>
                      <td className="px-3 py-2 font-mono">{d.event}</td>
                      <td className="px-3 py-2">
                        <span className={`rounded-full px-2 py-0.5 ${STATUS_BADGE[d.status]}`}>
                          {d.status}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-mono">{d.httpCode ?? '—'}</td>
                      <td className="px-3 py-2">{d.attempts}</td>
                      <td className="px-3 py-2 max-w-xs truncate text-[var(--color-muted)]" title={d.lastError ?? ''}>
                        {d.lastError ?? '—'}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {d.status !== 'pending' && (
                          <button
                            type="button"
                            onClick={() => void redeliver(openDeliveriesFor, d.id)}
                            className="text-[var(--color-t3-blue)] hover:underline"
                          >
                            Redeliver
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </>,
  );
}
